<script>
import AppButton from '@/components/ui/AppButton.vue'
import FormInput from '@/components/form/FormInput.vue'
import Icons from '@/components/Icons.vue'

export default {
	name: 'ProfileSecurity',
	components: {
		AppButton,
		FormInput,
		Icons
	},
	emits: ['cancel', 'dirty-change', 'edit', 'submit'],
	props: {
		email: {
			type: String,
			default: ''
		},
		isSaving: {
			type: Boolean,
			default: false
		},
		isEditing: {
			type: Boolean,
			default: false
		},
		isSuccess: {
			type: Boolean,
			default: false
		},
		requestError: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			form: {
				originalPassword: '',
				password: '',
				repeatPassword: ''
			},
			showCurrentPassword: false,
			showPassword: false,
			showRepeatPassword: false,
			submitted: false
		}
	},
	computed: {
		validation() {
			return {
				originalPassword: !!this.form.originalPassword,
				password: !!this.form.password,
				passLength: this.form.password.length >= 8 && this.form.password.length <= 100,
				passCharacters: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).+$/.test(this.form.password),
				passEmail: !!this.form.password && this.form.password.toLowerCase() !== this.email.toLowerCase(),
				passSequence: !!this.form.password && !this.sequenceValidation(this.form.password),
				repeatPassword: !!this.form.repeatPassword && this.form.repeatPassword === this.form.password
			}
		},
		isValid() {
			return Object.values(this.validation).every(Boolean)
		},
		isDirty() {
			return Object.values(this.form).some(value => !!value)
		},
		passwordRules() {
			return [
				{ key: 'length', valid: this.validation.passLength, label: this.$t('form.validPass.01') },
				{ key: 'characters', valid: this.validation.passCharacters, label: this.$t('form.validPass.02') },
				{ key: 'email', valid: this.validation.passEmail, label: this.$t('form.validPass.03') },
				{ key: 'sequence', valid: this.validation.passSequence, label: this.$t('form.validPass.04') }
			]
		},
		securityTips() {
			return ['birthDate', 'document', 'characters', 'reuse', 'share'].map(key => ({
				key,
				label: this.$t(`myArea.security.tips.${key}`)
			}))
		}
	},
	watch: {
		isDirty: {
			immediate: true,
			handler(value) {
				this.$emit('dirty-change', value)
			}
		},
		isEditing(value) {
			if (!value) {
				this.resetForm()
				return
			}

			this.submitted = false
		},
		isSuccess(value) {
			if (!value) return
			this.resetForm()
		}
	},
	methods: {
		resetForm() {
			this.form.originalPassword = ''
			this.form.password = ''
			this.form.repeatPassword = ''
			this.showCurrentPassword = false
			this.showPassword = false
			this.showRepeatPassword = false
			this.submitted = false
		},
		sequenceValidation(value) {
			const normalizedValue = value.toLowerCase()

			for (let index = 0; index < normalizedValue.length - 2; index++) {
				const first = normalizedValue.charCodeAt(index)
				const second = normalizedValue.charCodeAt(index + 1)
				const third = normalizedValue.charCodeAt(index + 2)

				if (second === first + 1 && third === first + 2) return true
			}

			return false
		},
		togglePassword(field) {
			this[field] = !this[field]
		},
		handlePrimaryAction() {
			if (this.isEditing) {
				this.submitPassword()
				return
			}

			this.submitted = false
			this.$emit('edit')
		},
		cancelEditing() {
			if (this.isSaving) return

			this.resetForm()
			this.$emit('cancel')
		},
		submitPassword() {
			if (!this.isEditing) return
			this.submitted = true
			if (!this.isValid || this.isSaving) return

			this.$emit('submit', { ...this.form })
		}
	}
}
</script>

<template>
	<section class="section__module profile__security-section profile__section-card" data-qa="profile-security-section" :aria-label="$t('myArea.menu.security')">
		<form class="profile-security" data-qa="profile-security-form" @submit.prevent="submitPassword">
				<div class="profile-security__form">
					<FormInput
						data-qa="profile-security-current-password-input"
						v-model="form.originalPassword"
						:label="$t('myArea.security.currentPasswordLabel')"
						:placeholder="$t('myArea.security.currentPasswordPlaceholder')"
						:input-type="showCurrentPassword ? 'text' : 'password'"
						:is-error="submitted && !validation.originalPassword"
						:message-error="$t('myArea.security.currentPasswordError')"
						:is-disabled="!isEditing"
						input-id="current-password"
						autocomplete="current-password"
					>
						<AppButton
							data-qa="profile-security-current-password-toggle"
							variant="password-toggle"
							:icon="showCurrentPassword ? 'eye' : 'eyeSlash'"
							:aria-label="showCurrentPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:title="showCurrentPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:disabled="!isEditing"
							@click="togglePassword('showCurrentPassword')"
						/>
					</FormInput>

					<FormInput
						data-qa="profile-security-new-password-input"
						v-model="form.password"
						:label="$t('form.labels.passwordNew')"
						:placeholder="$t('myArea.security.newPasswordPlaceholder')"
						:input-type="showPassword ? 'text' : 'password'"
						:is-error="submitted && !validation.password"
						:message-error="$t('form.messageError.passwordNew')"
						:is-disabled="!isEditing"
						input-id="new-password"
						autocomplete="new-password"
					>
						<AppButton
							data-qa="profile-security-new-password-toggle"
							variant="password-toggle"
							:icon="showPassword ? 'eye' : 'eyeSlash'"
							:aria-label="showPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:title="showPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:disabled="!isEditing"
							@click="togglePassword('showPassword')"
						/>
					</FormInput>

					<FormInput
						data-qa="profile-security-repeat-password-input"
						v-model="form.repeatPassword"
						:label="$t('myArea.security.repeatPasswordLabel')"
						:placeholder="$t('myArea.security.repeatPasswordPlaceholder')"
						:input-type="showRepeatPassword ? 'text' : 'password'"
						:is-error="submitted && !validation.repeatPassword"
						:message-error="$t('form.messageError.passwordNewRepeat')"
						:is-disabled="!isEditing"
						input-id="repeat-new-password"
						autocomplete="new-password"
					>
						<AppButton
							data-qa="profile-security-repeat-password-toggle"
							variant="password-toggle"
							:icon="showRepeatPassword ? 'eye' : 'eyeSlash'"
							:aria-label="showRepeatPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:title="showRepeatPassword ? $t('signIn.hidePassword') : $t('signIn.showPassword')"
							:disabled="!isEditing"
							@click="togglePassword('showRepeatPassword')"
						/>
					</FormInput>

					<ul v-show="isEditing" class="profile-security__rules" data-qa="profile-security-rules-list" :aria-label="$t('myArea.security.rulesAriaLabel')">
						<li
							v-for="rule in passwordRules"
							:key="rule.key"
							:class="{ 'is-valid': rule.valid }"
							data-qa="profile-security-rule-item"
							:data-qa-id="rule.key"
						>
							<Icons :name="rule.valid ? 'check' : 'xMark'" aria-hidden="true" />
							<span>{{ rule.label }}</span>
						</li>
					</ul>

					<div class="profile-security__interaction">
						<div class="profile-security__actions">
							<AppButton
								data-qa="profile-security-submit-button"
								type="button"
								:variant="isEditing ? 'primary' : 'secondary'"
								:disabled="isSaving"
								:aria-busy="isSaving"
								@click="handlePrimaryAction"
							>
								{{ $t(isSaving ? 'buttons.saving' : isEditing ? 'myArea.security.savePassword' : 'myArea.security.changePassword') }}
							</AppButton>
							<AppButton
								v-if="isEditing"
								data-qa="profile-security-cancel-button"
								type="button"
								variant="secondary"
								:disabled="isSaving"
								@click="cancelEditing"
							>
								{{ $t('buttons.cancelar') }}
							</AppButton>
						</div>
						<Transition name="profile-feedback">
							<p v-if="isSuccess" class="profile-security__alert profile-security__alert--success" data-qa="profile-security-success" role="status">
								<Icons name="check" aria-hidden="true" />
								{{ $t('myArea.security.success') }}
							</p>
						</Transition>
						<p v-if="!isSuccess && requestError" class="profile-security__alert profile-security__alert--error" data-qa="profile-security-error" role="alert">
							<Icons name="exclamation" aria-hidden="true" />
							{{ requestError }}
						</p>
					</div>
				</div>

				<aside class="profile-security__tips">
					<h2>{{ $t('myArea.security.tipsTitle') }}</h2>
					<ul data-qa="profile-security-tips-list">
						<li v-for="tip in securityTips" :key="tip.key" data-qa="profile-security-tip-item" :data-qa-id="tip.key">
							<Icons name="securityTipCheck" aria-hidden="true" />
							<span>{{ tip.label }}</span>
						</li>
					</ul>
				</aside>
		</form>
	</section>
</template>

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_responsive' as *

.profile-security
	display: grid
	grid-template-columns: minmax(0, 1fr)
	gap: 1.5rem
	align-items: start

	+respond-to($breakpoint-lg-min)
		grid-template-columns: minmax(0, 31.625rem) minmax(17.5rem, 20.75rem)

	&__form
		display: flex
		flex-direction: column
		gap: 0.75rem

		:deep(.box--form)
			width: 100%
			padding: 0

		:deep(.box--form input)
			padding-right: 2.5rem

	&__rules
		display: flex
		flex-direction: column
		gap: 0.5rem
		margin: 0.75rem 0 0
		padding: 0.75rem
		border-radius: 0.5rem
		background-color: var(--lg-light-gray-200)
		list-style: none

		li
			display: flex
			align-items: flex-start
			gap: 0.25rem
			color: var(--lg-red-active)
			font-family: var(--lgei-text)
			font-size: 0.75rem
			line-height: 1.25

			&.is-valid
				color: var(--green-10)

		i
			width: 0.75rem
			height: 0.75rem
			flex: 0 0 0.75rem

	&__interaction
		display: flex
		flex-direction: column
		gap: 1rem
		align-items: flex-start
		margin-top: 0.75rem

		+respond-to($breakpoint-lg-min)
			flex-direction: row
			align-items: center

	&__actions
		display: flex
		flex-wrap: wrap
		gap: 0.75rem

	&__alert
		display: flex
		align-items: center
		gap: 0.5rem
		min-height: 2.5rem
		margin: 0
		padding: 0.5rem 1rem
		border-radius: 0.5rem
		font-family: var(--lgei-text)
		font-size: 0.875rem

		i
			width: 1.25rem
			height: 1.25rem

		&--success
			border: 1px solid rgba(var(--lg-security-tip-green-rgb), 0.4)
			background-color: var(--lg-security-tip-background)
			box-shadow: var(--shadow-default)
			color: var(--lg-dark-gray-200)

			i
				color: var(--lg-security-tip-green)

		&--error
			background-color: rgba(var(--lg-red-active-rgb), 0.1)
			color: var(--lg-red-active)

	&__tips
		width: 100%
		max-width: 20.75rem
		min-height: 14rem
		padding: 1rem
		border: 1px solid rgba(var(--lg-security-tip-green-rgb), 0.1)
		border-radius: 0.5rem
		background-color: var(--lg-security-tip-background)
		box-shadow: 0 0.0625rem 0.125rem rgba(var(--color-grey-2000-rgb), 0.04)

		h2
			margin: 0 0 1rem
			color: var(--lg-dark-gray-300)
			font-family: var(--lgei-headline)
			font-size: 1.25rem
			font-weight: 400
			line-height: 1

		ul
			display: flex
			flex-direction: column
			gap: 0.5rem
			margin: 0
			padding: 0
			list-style: none

		li
			display: flex
			align-items: center
			gap: 0.25rem
			color: var(--lg-mid-gray-300)
			font-family: var(--lgei-text)
			font-size: 0.875rem
			line-height: 1.4

		i
			width: 1.5rem
			height: 1.5rem
			flex: 0 0 1.5rem
			color: var(--lg-security-tip-green)

.profile-feedback-enter-active,
.profile-feedback-leave-active
	transition: opacity 0.2s ease, transform 0.2s ease

.profile-feedback-enter-from,
.profile-feedback-leave-to
	opacity: 0
	transform: translateY(0.25rem)

@media (prefers-reduced-motion: reduce)
	.profile-feedback-enter-active,
	.profile-feedback-leave-active
		transition: none
</style>
